import { useState } from 'react';
import { toast } from 'sonner';
import { useConfirm } from '../../context/ConfirmContext';
import {
  useSessionList,
  useForceLogoutSession,
  useForceLogoutUser,
  QuerySessionsDto,
  UserSession,
} from '../../api/admin';

function parseUserAgent(userAgent: string | null): { browser: string; os: string } {
  if (!userAgent) return { browser: 'Unbekannt', os: 'Unbekannt' };

  let browser = 'Unbekannt';
  let os = 'Unbekannt';

  // Browser detection
  if (userAgent.includes('Chrome')) browser = 'Chrome';
  else if (userAgent.includes('Firefox')) browser = 'Firefox';
  else if (userAgent.includes('Safari')) browser = 'Safari';
  else if (userAgent.includes('Edge')) browser = 'Edge';
  else if (userAgent.includes('Opera')) browser = 'Opera';

  // OS detection
  if (userAgent.includes('Windows')) os = 'Windows';
  else if (userAgent.includes('Mac')) os = 'macOS';
  else if (userAgent.includes('Linux')) os = 'Linux';
  else if (userAgent.includes('Android')) os = 'Android';
  else if (userAgent.includes('iOS') || userAgent.includes('iPhone')) os = 'iOS';

  return { browser, os };
}

export function SessionsPage() {
  const confirm = useConfirm();
  const [query, setQuery] = useState<QuerySessionsDto>({ page: 1, limit: 20 });

  const { data, isLoading } = useSessionList(query);
  const forceLogoutSession = useForceLogoutSession();
  const forceLogoutUser = useForceLogoutUser();

  const handleLogoutSession = async (session: UserSession) => {
    const confirmed = await confirm({
      title: 'Sitzung beenden',
      message: `Möchten Sie die Sitzung von "${session.user.firstName} ${session.user.lastName}" wirklich beenden?`,
      confirmLabel: 'Beenden',
      variant: 'danger',
    });
    if (confirmed) {
      try {
        await forceLogoutSession.mutateAsync(session.id);
        toast.success('Sitzung beendet');
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Fehler beim Beenden der Sitzung');
      }
    }
  };

  const handleLogoutAllUserSessions = async (session: UserSession) => {
    const confirmed = await confirm({
      title: 'Alle Sitzungen beenden',
      message: `Möchten Sie ALLE Sitzungen von "${session.user.firstName} ${session.user.lastName}" wirklich beenden?`,
      confirmLabel: 'Alle beenden',
      variant: 'danger',
    });
    if (confirmed) {
      try {
        await forceLogoutUser.mutateAsync(session.userId);
        toast.success('Alle Sitzungen beendet');
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Fehler beim Beenden der Sitzungen');
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Gerade eben';
    if (minutes < 60) return `vor ${minutes} Min.`;
    if (hours < 24) return `vor ${hours} Std.`;
    return `vor ${days} Tag(en)`;
  };

  const handlePageChange = (newPage: number) => {
    setQuery((prev) => ({ ...prev, page: newPage }));
  };

  // Group sessions by user
  const sessionsByUser = data?.data.reduce((acc: Record<string, UserSession[]>, session: UserSession) => {
    const key = session.userId;
    if (!acc[key]) acc[key] = [];
    acc[key].push(session);
    return acc;
  }, {} as Record<string, UserSession[]>) || {};

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Aktive Sitzungen</h1>
        <p className="mt-1 text-sm text-gray-500">
          Überwachen und verwalten Sie aktive Benutzersitzungen
        </p>
      </div>

      {/* Stats */}
      {data?.meta && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Aktive Sitzungen</dt>
                    <dd className="text-lg font-semibold text-gray-900">{data.meta.total}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Eingeloggte Benutzer</dt>
                    <dd className="text-lg font-semibold text-gray-900">{Object.keys(sessionsByUser).length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Sitzungen/Benutzer</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {Object.keys(sessionsByUser).length > 0
                        ? (data.meta.total / Object.keys(sessionsByUser).length).toFixed(1)
                        : '0'}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Laden...</p>
          </div>
        ) : data?.data.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Keine aktiven Sitzungen</h3>
            <p className="mt-1 text-sm text-gray-500">
              Es sind derzeit keine Benutzer angemeldet.
            </p>
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Benutzer
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Browser / System
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP-Adresse
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Letzte Aktivität
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sitzungsstart
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Aktionen</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data?.data.map((session: UserSession) => {
                  const { browser, os } = parseUserAgent(session.userAgent);
                  const userSessionCount = sessionsByUser[session.userId]?.length || 1;
                  return (
                    <tr key={session.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-primary-600 font-medium text-sm">
                                {session.user.firstName[0]}{session.user.lastName[0]}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {session.user.firstName} {session.user.lastName}
                            </div>
                            <div className="text-sm text-gray-500">{session.user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{browser}</div>
                        <div className="text-sm text-gray-500">{os}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-mono">{session.ipAddress || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{getRelativeTime(session.lastActiveAt)}</div>
                        <div className="text-sm text-gray-500">{formatDate(session.lastActiveAt)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(session.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleLogoutSession(session)}
                            disabled={forceLogoutSession.isPending}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          >
                            Beenden
                          </button>
                          {userSessionCount > 1 && (
                            <button
                              onClick={() => handleLogoutAllUserSessions(session)}
                              disabled={forceLogoutUser.isPending}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                              title={`${userSessionCount} aktive Sitzungen`}
                            >
                              Alle ({userSessionCount})
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {data?.meta && data.meta.totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Zeige <span className="font-medium">{(data.meta.page - 1) * data.meta.limit + 1}</span>
                      {' '}-{' '}
                      <span className="font-medium">{Math.min(data.meta.page * data.meta.limit, data.meta.total)}</span>
                      {' '}von <span className="font-medium">{data.meta.total}</span> Sitzungen
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(data.meta!.page - 1)}
                        disabled={data.meta!.page <= 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        Seite {data.meta.page} von {data.meta.totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(data.meta!.page + 1)}
                        disabled={data.meta!.page >= data.meta!.totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default SessionsPage;
